import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Trash2, Edit, Eye, Upload, Image } from 'lucide-react';
import Layout from '@/components/Layout';
import IconPicker from '@/components/IconPicker';
import MediaPickerModal from '@/components/MediaPickerModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CourseEditorPage() {
  const { id } = useParams(); // If id exists, we're editing
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUnsplashPicker, setShowUnsplashPicker] = useState(false);
  const [testCategories, setTestCategories] = useState([]);
  
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    price: 0,
    tier: 'basic',
    category: '',
    author: '',
    duration_hours: 0,
    status: 'published', // Changed default to published
    cover_image_url: '',
    date: new Date().getFullYear().toString(), // Add required date field
    display_icon: 'Heart' // Default icon
  });

  useEffect(() => {
    loadTestCategories();
    if (id) {
      loadCourse();
    }
  }, [id]);

  const loadTestCategories = async () => {
    try {
      const response = await axios.get(`${API}/test-categories`);
      setTestCategories(response.data.filter(cat => cat.enabled));
    } catch (error) {
      console.error('Failed to load test categories:', error);
    }
  };

  const loadCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${id}`, {
        withCredentials: true
      });
      setCourseForm(response.data);
    } catch (error) {
      toast.error('Failed to load course');
      navigate('/admin');
    }
  };

  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/admin/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Set the uploaded image URL as the cover image
      const imageUrl = `${BACKEND_URL}${response.data.image_url}`;
      setCourseForm(prev => ({ 
        ...prev, 
        cover_image_url: imageUrl 
      }));
      
      toast.success('Cover image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload cover image');
    } finally {
      setUploading(false);
    }
  };

  const handleImageSelect = (url) => {
    setCourseForm(prev => ({
      ...prev,
      cover_image_url: url
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (id) {
        // Update existing course
        await axios.put(`${API}/admin/courses/${id}`, courseForm, {
          withCredentials: true
        });
        toast.success('Course updated successfully!');
      } else {
        // Create new course
        const response = await axios.post(`${API}/admin/courses`, courseForm, {
          withCredentials: true
        });
        toast.success('Course created successfully!');
        // Navigate to edit page with the new course ID
        navigate(`/admin/courses/${response.data.id}/edit`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save course');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin');
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#F4E4C1] to-[#E8B67A] py-8">
        <div className="max-w-6xl mx-auto px-4">
          
          {/* Header with Navigation */}
          <div className="cassette-card p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleCancel}
                  className="retro-button-small flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Admin
                </button>
                <h1 className="text-4xl retro-title text-[#2C1810]">
                  {id ? 'Edit Course' : 'Create New Course'}
                </h1>
              </div>
              
              <div className="flex gap-3">
                {id && (
                  <button
                    onClick={() => navigate(`/courses/${id}`)}
                    className="retro-button-small flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Preview
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="retro-button flex items-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  {loading ? 'Saving...' : 'Save Course'}
                </button>
              </div>
            </div>
          </div>

          {/* Course Details Form */}
          <div className="cassette-card p-8 mb-6">
            <h2 className="text-2xl font-bold text-[#2C1810] mb-6">Course Details</h2>
            
            <form onSubmit={handleSave} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Title */}
                <div className="md:col-span-2">
                  <label className="block text-[#2C1810] font-bold mb-2">Course Title *</label>
                  <input
                    type="text"
                    value={courseForm.title}
                    onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-[#6B4423] rounded bg-white text-[#2C1810] text-lg"
                    placeholder="e.g., Introduction to Traditional Chinese Medicine"
                    required
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-[#2C1810] font-bold mb-2">Description *</label>
                  <textarea
                    value={courseForm.description}
                    onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                    rows="5"
                    placeholder="Describe what students will learn in this course..."
                    required
                  />
                </div>

                {/* Tier */}
                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Tier *</label>
                  <select
                    value={courseForm.tier}
                    onChange={(e) => setCourseForm({ ...courseForm, tier: e.target.value, category: '' })}
                    className="w-full px-4 py-3 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                    required
                  >
                    <option value="basic">Basic Practice</option>
                    <option value="comps_ready">Comp's Ready</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Category *</label>
                  <select
                    value={courseForm.category}
                    onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                    required
                  >
                    <option value="">Select Category</option>
                    {testCategories
                      .filter(cat => cat.tier === courseForm.tier)
                      .map(cat => (
                        <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                      ))}
                  </select>
                </div>

                {/* Author */}
                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Instructor/Author *</label>
                  <input
                    type="text"
                    value={courseForm.author}
                    onChange={(e) => setCourseForm({ ...courseForm, author: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                    placeholder="e.g., Dr. John Smith"
                    required
                  />
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Duration (hours)</label>
                  <input
                    type="number"
                    value={courseForm.duration_hours}
                    onChange={(e) => setCourseForm({ ...courseForm, duration_hours: parseFloat(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                    placeholder="e.g., 10"
                    step="0.5"
                    min="0"
                  />
                </div>

                {/* Course Date/Year */}
                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Course Year *</label>
                  <input
                    type="text"
                    value={courseForm.date}
                    onChange={(e) => setCourseForm({ ...courseForm, date: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                    placeholder="e.g., 2024"
                    required
                  />
                </div>

                {/* Price */}
                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Price ($)</label>
                  <input
                    type="number"
                    value={courseForm.price}
                    onChange={(e) => setCourseForm({ ...courseForm, price: parseFloat(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                    placeholder="0 for free"
                    step="0.01"
                    min="0"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-[#2C1810] font-bold mb-2">Status</label>
                  <select
                    value={courseForm.status}
                    onChange={(e) => setCourseForm({ ...courseForm, status: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="coming_soon">Coming Soon</option>
                  </select>
                </div>

                {/* Cover Image */}
                <div className="md:col-span-2">
                  <label className="block text-[#2C1810] font-bold mb-2">Cover Image</label>
                  <button
                    type="button"
                    onClick={() => setShowUnsplashPicker(true)}
                    className="retro-button bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-2"
                  >
                    <Image className="w-5 h-5" />
                    Choose Image
                  </button>
                  {courseForm.cover_image_url && (
                    <div className="mt-3">
                      <img
                        src={courseForm.cover_image_url}
                        alt="Cover image preview"
                        className="h-32 w-48 object-cover rounded border-2 border-[#6B4423]"
                      />
                      <p className="text-xs text-[#6B4423] mt-1">Selected cover image</p>
                    </div>
                  )}
                </div>
                
                {/* Icon Picker for Course */}
                <div className="md:col-span-2">
                  <IconPicker
                    selectedIcon={courseForm.display_icon}
                    onSelectIcon={(icon) => setCourseForm({ ...courseForm, display_icon: icon })}
                  />
                  <p className="text-[#6B4423] text-xs mt-2">This icon will be used as the cover if no image is uploaded</p>
                </div>
              </div>
            </form>
          </div>

        </div>
      </div>

      {/* Unsplash Image Picker Modal */}
      <MediaPickerModal
        isOpen={showUnsplashPicker}
        onClose={() => setShowUnsplashPicker(false)}
        onSelect={handleImageSelect}
      />
    </Layout>
  );
}
